import React from "react";
import { Video, Calendar, ExternalLink } from "lucide-react";
import { formatInZone, isSafeHttpsUrl } from "../lib/timezone";

// Client's audience is Saudi-based, so Saudi Arabia Standard Time leads —
// same instant as 7:00 PM IST (the zone the class is actually scheduled/
// admin-entered in, see LIVE_CLASS_TIMEZONE in AdminReleaseManager.jsx),
// just displayed AST-first since that's the zone guardians here read in.
// (The per-viewer conversion machinery still exists in lib/timezone.js —
// formatInViewerLocal — if that's ever wanted back.)
const DISPLAY_ZONE = "Asia/Riyadh";

// IST (the zone it's actually scheduled in) and MYT (Malaysia guardians)
// still shown alongside AST, not instead of it, so nobody has to convert
// by hand.
const EXTRA_ZONES = ["Asia/Kolkata", "Asia/Kuala_Lumpur"]; // IST, MYT

// A plain video-camera icon, colored in Zoom's own brand blue rather than
// the app's green, so it reads as "this joins a video call" at a glance.
// Tried Zoom's own logotype glyph here first (Simple Icons project) — it's
// a wordmark, not a symbol, and came out as an illegible smudge of tiny
// cursive text at icon size (confirmed live, 2026-08-19). A plain,
// unmistakable camera shape works at any size; "Live class" + "Join" next
// to it already say what it's for, the color is what ties it to Zoom.
const ZOOM_BLUE = "#2D8CFF";

// Renders AST as the primary line, then "· 7:00 PM IST · 9:30 PM MYT"
// after it — same instant, no repeated date. Skipped entirely if there's
// no schedule yet (nothing meaningful to convert).
function ExtraZoneTimes({ isoUtc, className }) {
    if (!isoUtc) return null;
    return (
        <>
            {EXTRA_ZONES.map((zone) => (
                <span key={zone} className={className}>
                    · {formatInZone(isoUtc, zone, { showDate: false })}
                </span>
            ))}
        </>
    );
}

// One shared "Live Class" card, rendered from both Landing.jsx (signed-out
// visitors, fetches /settings/live-class itself) and Dashboard.jsx (reads
// it off the signed-in guardian's own user.live_class, same shape). Takes
// the config as a plain prop -- this component never fetches anything
// itself, so there's exactly one place the card's look/behavior lives
// instead of two drifting copies.
//
// `compact`: both Landing.jsx and Dashboard.jsx now pass this — the full
// stacked card (still below, `compact = false`) was the original look,
// kept around rather than deleted in case a future spot ever wants the
// bigger version back, but nothing currently renders it.
export default function LiveClassCard({ config, compact = false }) {
    const enabled = !!config?.enabled;
    const url = config?.meeting_url || "";
    // Defense in depth: the backend already validated this on save, but a
    // config record saved before that validation existed (or edited
    // directly in the DB) shouldn't get a free pass into a clickable link.
    const safeUrl = isSafeHttpsUrl(url) ? url : null;
    const hasSchedule = !!config?.scheduled_at;
    const isReady = enabled && !!safeUrl && hasSchedule;

    if (compact) {
        return (
            <div
                // h-full: lets this fill a stretched flex row's full
                // height when paired with WhatsAppCommunityCard on
                // Dashboard.jsx (their content heights genuinely differ —
                // this can wrap onto two lines with three timezones shown,
                // that card never does) — harmless on Landing.jsx, where
                // there's no sibling row to stretch against anyway.
                className="rounded-full bg-white border border-honeySoft shadow-sm shadow-black/5 px-4 sm:px-5 py-2.5 flex items-center gap-3 flex-wrap sm:flex-nowrap h-full"
                data-testid="live-class-card"
            >
                <div className="rounded-full bg-[#2D8CFF]/10 p-1.5 shrink-0">
                    <Video className="w-4 h-4" style={{ color: ZOOM_BLUE }} strokeWidth={2.5} />
                </div>

                {isReady ? (
                    // flex-col of two explicitly grouped rows, not one flat
                    // flex-wrap of four independent items (label, title,
                    // date line, button) — that flat version let the browser
                    // wrap wherever it individually felt like, so the Join
                    // button sometimes landed on its own disconnected third
                    // line with a slab of empty pill space around it while
                    // "Live class:" sat pinned awkwardly close to the top
                    // corner (confirmed live via a screenshot). Grouping
                    // label+title as their own row and date+button as their
                    // own row means the button always stays visually
                    // anchored right after the date/time text, wrapping
                    // below it (not off on a random line elsewhere) only if
                    // it truly doesn't fit.
                    <div className="flex flex-col gap-1.5 min-w-0 flex-1" data-testid="live-class-active">
                        <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                            <p className="font-display font-bold text-charcoal text-sm sm:text-base shrink-0">Live class:</p>
                            {/* No truncate here on purpose — a flex-shrink
                                item with a nowrap/ellipsis style clips at
                                ONE line worth of width even when flex-wrap
                                has plenty of room across a second line
                                (confirmed live: a genuinely long title still
                                clipped at both tablet and phone widths
                                despite the row itself wrapping). Plain
                                wrapping text has no such ceiling — it just
                                uses another line, same as the date/time
                                piece below already does. */}
                            <p className="font-semibold text-charcoal text-sm sm:text-base" data-testid="live-class-title">
                                {config.title || "Join our live class"}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            {/* min-w-0, not shrink-0 — with three zones now
                                (IST + MYT + KSA) this line is genuinely too
                                wide to ever fit unbroken on a narrow phone
                                (confirmed live: 320px-wide screens, e.g.
                                iPhone SE). shrink-0 blocked this flex item
                                from shrinking at all, which forced the
                                WHOLE PAGE 39px wider than the actual
                                viewport instead of just wrapping this one
                                line onto two — every other fixed/w-full
                                element on the page (like a modal) then
                                measured itself against that artificially
                                widened layout viewport instead of the real
                                one. */}
                            <p className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-brightGreen min-w-0 flex-wrap" data-testid="live-class-time">
                                <Calendar className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                                {formatInZone(config.scheduled_at, DISPLAY_ZONE)}
                                <ExtraZoneTimes isoUtc={config.scheduled_at} className="text-brightGreen font-semibold" />
                            </p>
                            <a
                                href={safeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="live-class-join"
                                className="inline-flex items-center gap-1.5 rounded-full bg-brightGreen text-white px-4 py-1.5 text-sm font-display font-bold shadow-sm shadow-brightGreen/20 hover:brightness-105 shrink-0 sm:ml-auto"
                            >
                                Join <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.8} />
                            </a>
                        </div>
                    </div>
                ) : (
                    <p className="text-mocha text-sm sm:text-base font-semibold" data-testid="live-class-empty">
                        No live class scheduled yet — check back soon.
                    </p>
                )}
            </div>
        );
    }

    // Full card — Dashboard.jsx's look, unchanged from before `compact`
    // existed. Both the "active" and "not scheduled" states render the
    // same outer card shape/height on purpose, so toggling it on/off in
    // the admin panel never makes the page jump.
    return (
        <div
            className="rounded-[2rem] bg-white border border-honeySoft p-6 sm:p-7 shadow-md shadow-black/5"
            data-testid="live-class-card"
        >
            <div className="flex items-center gap-3 mb-4">
                <div className="rounded-2xl bg-[#2D8CFF]/10 p-2.5 shrink-0">
                    <Video className="w-5 h-5" style={{ color: ZOOM_BLUE }} strokeWidth={2.5} />
                </div>
                <p className="font-display text-lg sm:text-xl font-bold text-deepGreen">Live class</p>
            </div>

            {isReady ? (
                <div data-testid="live-class-active">
                    <p className="font-display text-xl sm:text-2xl font-bold text-charcoal leading-snug">
                        {config.title || "Join our live class"}
                    </p>
                    {config.description && (
                        <p className="text-mocha text-sm sm:text-base mt-2 leading-relaxed">{config.description}</p>
                    )}
                    {/* `flex` here, not `inline-flex` — inline-flex makes the
                        <p> itself an inline-level box, so it shares a line
                        with the Join button below instead of stacking above
                        it, and a longer formatted date/time (some locales/
                        zones run noticeably longer, e.g. "Tue, Aug 18,
                        8:55 PM IST") visibly overlapped the button's edge. */}
                    <p className="flex items-center gap-2 text-sm font-semibold text-brightGreen mt-4 flex-wrap" data-testid="live-class-time">
                        <Calendar className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                        {formatInZone(config.scheduled_at, DISPLAY_ZONE)}
                        <ExtraZoneTimes isoUtc={config.scheduled_at} className="text-brightGreen font-semibold" />
                    </p>
                    <a
                        href={safeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="live-class-join"
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-brightGreen text-white px-6 py-3 font-display font-bold shadow-md shadow-brightGreen/20 hover:brightness-105"
                    >
                        Join <ExternalLink className="w-4 h-4" strokeWidth={2.8} />
                    </a>
                </div>
            ) : (
                <div data-testid="live-class-empty">
                    <p className="font-display text-xl sm:text-2xl font-bold text-charcoal/50 leading-snug">
                        No class scheduled yet
                    </p>
                    <p className="text-mocha text-sm sm:text-base mt-2 leading-relaxed">
                        Check back soon — we'll post the next live class here.
                    </p>
                </div>
            )}
        </div>
    );
}
